import React from "react";
import { useAuth, useUser } from "@clerk/nextjs";
import { ButtonVariant, CoButton } from "components/CoButton";
import { Button, Heading, Text } from "design_system";
import LargeHumanTrialIcon from "design_system/color_icons/LargeHumanTrialIcon";
import { X } from "lucide-react";
import StackedEmptyModal from "components/Modal/StackedEmptyModal";
import { ComposedModal } from "components/Modal";
import {
  PricingSwitch,
  useSubscriptionProducts,
  useSubscriptionUpgradeTarget,
  useSubscriptionPricing,
  SubscriptionPlanIcon,
  getSubscriptionPlanLabel,
} from "features/Pricing";
import { PricingSwitchFlag } from "features/Pricing/components/PricingSwitch";
import path from "constants/path";
import { SubscriptionPlan } from "enums/subscription-plans";
import useSubscription from "features/User/hooks/useSubscription";
import { unPauseSubscription } from "helpers/churnkey";
import { formatDateString } from "helpers/date";
import { formatCurrency } from "helpers/format";
import { pricingPageUrl, searchPageUrl } from "helpers/pageUrl";
import {
  isSubscriptionPaused,
  isSubscriptionPausedAndNotPremium,
  isBillingAdminSubscription,
  UserSubscription,
} from "helpers/subscription";
import useLabels from "hooks/useLabels";
import { useRouter } from "next/router";
import { useMemo, useState } from "react";

import { useSubscriptionRedirect } from "../SubscriptionProvider/SubscriptionProvider";
import { usePremiumFeatureModal } from "./usePremiumFeatureModal";
import PricingFeatures from "features/Pricing/components/PricingFeatures";
import PricingCard from "features/Pricing/components/PricingCard/PricingCard";
import Link from "next/link";
import StudentDiscountBanner from "../StudentDiscountBanner";
import { getUserStudentEmail } from "helpers/email";
import { MixpanelActionSource } from "helpers/services/mixpanel/events";
import { UserResource } from "@clerk/types";

export const PREMIUM_FEATURE_MODAL_ID = "premium-feature-modal";
/**
 * @component PremiumFeatureModal
 * @description Component for premium feature modal
 * @example
 * return (
 *   <PremiumFeatureModal />
 * )
 */
function PremiumFeatureModal() {
  const router = useRouter();

  const { products } = useSubscriptionProducts();
  const { isSignedIn, isLoaded } = useAuth();
  const {
    currentFeature,
    forceClosePremiumFeatureModal,
    closeAllModals,
    onClosePremiumFeatureModal,
    trackPaywallButtonClicked,
  } = usePremiumFeatureModal();

  const [modalLabels, featureLabels] = useLabels(
    "premium-feature-modal",
    "credits.features"
  );
  const [activeFlag, setActiveFlag] = useState(PricingSwitchFlag.ANNUALLY);
  const {
    redirectToCheckout,
    redirectToCustomerPortalSubscriptionUpdateConfirm,
  } = useSubscriptionRedirect();

  const { subscription } = useSubscription();
  const userSubscription = subscription?.user as UserSubscription;
  const { user } = useUser();
  const studentEmail = getUserStudentEmail(user as unknown as UserResource);
  // Use the new hook to get upgrade path information
  const { target, features, comparisonPlan, churnkeyFeatures } =
    useSubscriptionUpgradeTarget();

  // Alias for backwards compatibility with existing code
  const upgradeTarget = target;
  const features_list = features;
  const churnkeyFeatures_list = churnkeyFeatures;

  // Get current plan
  const currentPlan =
    subscription === undefined
      ? undefined
      : subscription.team ||
          isBillingAdminSubscription(subscription.user as UserSubscription)
        ? SubscriptionPlan.TEAM
        : subscription.org
          ? SubscriptionPlan.ENTERPRISE
          : subscription.user
            ? SubscriptionPlan.PREMIUM
            : SubscriptionPlan.FREE;

  const resumesAt = useMemo(() => {
    if (isSubscriptionPaused(subscription?.user)) {
      // @ts-ignore pause_collection will never be null if subscription is paused
      return new Date(userSubscription.pause_collection.resumes_at * 1000);
    }
    return new Date();
  }, [subscription?.user, userSubscription]);

  const planPrice = useMemo(() => {
    if (isSubscriptionPaused(userSubscription)) {
      return {
        amount: userSubscription.plan.amount,
        interval: userSubscription.plan.interval,
      };
    }
    return { amount: 0, interval: "month" };
  }, [userSubscription]);

  const isAnnualSelected = activeFlag === PricingSwitchFlag.ANNUALLY;

  // Use the new pricing hook for the upgrade target
  const { baseMonthlyPrice, baseYearlyPrice } =
    useSubscriptionPricing(upgradeTarget);

  const handleChurnkeyUnpause = async (label: string) => {
    trackPaywallButtonClicked(label);
    closeAllModals();

    if (isSubscriptionPaused(userSubscription)) {
      // @ts-ignore pause_collection will never be null if subscription is paused
      await unPauseSubscription(userSubscription);
    }
  };

  const handleClickUpgrade = async (label: string) => {
    trackPaywallButtonClicked(label);
    closeAllModals();

    let priceId: string | undefined;

    if (activeFlag == PricingSwitchFlag.MONTHLY && baseMonthlyPrice) {
      priceId = baseMonthlyPrice.id;
    }

    if (isAnnualSelected && baseYearlyPrice) {
      priceId = baseYearlyPrice.id;
    }

    if (!isLoaded) return;

    if (!isSignedIn) {
      router.push(
        `${path.SIGN_UP}#/?redirect_url=${encodeURIComponent(
          `${pricingPageUrl(priceId)}`
        )}`
      );
      return;
    }

    if (!priceId) {
      console.error("No price ID available for checkout");
      return;
    }

    // If user has an existing subscription, update it through the customer portal
    if (subscription?.user) {
      const subscriptionId = subscription.user.id;
      const subscriptionItemId = subscription.user.items?.data?.[0]?.id;

      if (subscriptionItemId) {
        await redirectToCustomerPortalSubscriptionUpdateConfirm({
          subscription: subscriptionId as string,
          subscriptionItemId,
          newPriceId: priceId,
          returnUrl: searchPageUrl(),
        });
      } else {
        console.error("No subscription item ID found for update");
      }
    } else {
      // For new subscriptions, use checkout
      await redirectToCheckout({
        price: priceId,
      });
    }
  };

  const title =
    currentFeature !== undefined && featureLabels[currentFeature]
      ? modalLabels["title-feature"].replace(
          "{FEATURE}",
          featureLabels[currentFeature]["credit-type"]
        )
      : modalLabels["title"];

  const footer = (
    <div className="flex items-center gap-2 p-4 border-t">
      <LargeHumanTrialIcon className="text-fg-muted" />
      <Link
        href={pricingPageUrl()}
        onClick={() => {
          trackPaywallButtonClicked("view-more-plans");
          closeAllModals();
          router.push(pricingPageUrl());
        }}
      >
        <Text size="sm" theme="muted" className="underline underline-offset-4">
          View more plans
        </Text>
      </Link>
    </div>
  );

  const renderPausedMessage = () => {
    return (
      <StackedEmptyModal
        modal={{ id: PREMIUM_FEATURE_MODAL_ID, blocksScroll: true }}
        onClose={() => onClosePremiumFeatureModal(true)}
      >
        <ComposedModal size="base">
          <ComposedModal.Header
            title={modalLabels["resume-unlimited-access"]}
            onClose={() => forceClosePremiumFeatureModal()}
          />
          <ComposedModal.Body>
            <div className="m-4"></div>
          </ComposedModal.Body>
          <div className="overflow-auto flex-1">
            <div className="m-4">
              <div className="self-stretch px-4 py-3 bg-bg-base rounded-xl outline-solid outline-1 -outline-offset-1 outline-border-base inline-flex justify-start items-center gap-4">
                <div className="overflow-hidden relative w-6 h-6">
                  <img
                    src={`/icons/pause.svg`}
                    alt="pause"
                    className="w-5 h-5"
                  />
                </div>
                <div className="flex-1 inline-flex flex-col justify-start items-start gap-0.5">
                  <h3 className="sm-md">
                    {modalLabels["subscription-is-paused"]}
                  </h3>
                  <p className="sm-normal text-fg-muted">
                    {modalLabels["automatically-resumes"].replace(
                      "{DATE}",
                      formatDateString(resumesAt)
                    )}
                  </p>
                </div>
              </div>
              <div
                style={{ backgroundSize: "initial" }}
                className="flex flex-col gap-4 p-4 mt-4 bg-contain border rounded-xl bg-accent-bg-base/8 border-accent-bg-base/24"
              >
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between">
                    <div className="flex gap-3 items-center">
                      <SubscriptionPlanIcon plan={SubscriptionPlan.PREMIUM} />
                      <h3 className="base-bold">{modalLabels["premium"]}</h3>
                    </div>
                  </div>
                  <p className="sm-normal text-fg-muted">
                    {modalLabels["premium-description"]}
                  </p>
                </div>
                <div>
                  <span className="lg-bold text-fg-base">
                    {formatCurrency(planPrice.amount / 100)}
                  </span>
                  <span className="ml-1 text-fg-muted">
                    {modalLabels["per-month"]}
                  </span>
                </div>
                <div className="sticky">
                  <CoButton
                    variant={ButtonVariant.Primary}
                    size="sm"
                    onClick={() =>
                      handleChurnkeyUnpause(modalLabels["resume-subscription"])
                    }
                    className="w-full"
                    label={modalLabels["resume-subscription"]}
                  />
                </div>
                <div className="flex flex-wrap gap-y-2 feature-list">
                  <PricingFeatures
                    featuresList={churnkeyFeatures_list || features_list}
                  />
                </div>
              </div>
            </div>
          </div>
        </ComposedModal>
      </StackedEmptyModal>
    );
  };

  if (isSubscriptionPausedAndNotPremium(userSubscription)) {
    return renderPausedMessage();
  }

  return (
    <StackedEmptyModal
      modal={{ id: PREMIUM_FEATURE_MODAL_ID, blocksScroll: true }}
      onClose={() => onClosePremiumFeatureModal(true)}
    >
      <ComposedModal size="lg">
        <div className="py-3 pl-4 pr-1.5 border-b">
          <div className="flex items-stretch gap-2">
            <div className="flex flex-1 sm:items-center justify-between flex-col sm:flex-row gap-2 sm:gap-0">
              <Heading as="h1" size="x-sm">
                {title}
              </Heading>
              <div>
                <PricingSwitch
                  onChange={(currentFlag) => {
                    setActiveFlag(currentFlag);
                    trackPaywallButtonClicked(currentFlag);
                  }}
                  value={activeFlag}
                />
              </div>
            </div>
            <Button
              variant="flat"
              size="base"
              onClick={() => forceClosePremiumFeatureModal()}
              iconOnly
              iconLeft={<X />}
              data-testid="close-button"
            >
              Close
            </Button>
          </div>
        </div>
        <div className="overflow-auto flex-1 flex flex-col">
          {studentEmail && (
            <StudentDiscountBanner
              className="p-6 border-b"
              actionSource={MixpanelActionSource.PremiumFeatureModal}
            />
          )}
          {/* Pricing cards side by side with 3:4 ratio */}
          <div className="flex flex-col-reverse sm:flex-row">
            {/* Comparison plan - 3 parts width */}
            <div className="sm:flex-[3] py-6 sm:px-6 px-4 sm:border-r border-border-base">
              <PricingCard
                title={getSubscriptionPlanLabel(comparisonPlan)}
                plan={comparisonPlan.toLowerCase()}
                currentPlan={currentPlan}
                isAnnualSelected={isAnnualSelected}
                onClick={() => {
                  trackPaywallButtonClicked(
                    `keep-plan ${comparisonPlan.toLowerCase()}`
                  );
                  forceClosePremiumFeatureModal();
                }}
                isSignedIn={isSignedIn}
                buttonOverride={{
                  text: modalLabels["keep-plan"].replace(
                    "{PLAN}",
                    getSubscriptionPlanLabel(comparisonPlan)
                  ),
                  variant: "tertiary",
                  isDisabled: false,
                }}
              />
            </div>

            {/* Upgrade target plan - 4 parts width */}
            <div className="sm:flex-[4] py-6 sm:px-6 px-4 sm:border-0 border-b border-border-base">
              <PricingCard
                title={getSubscriptionPlanLabel(upgradeTarget)}
                plan={upgradeTarget.toLowerCase()}
                product={
                  products?.find(
                    (p: any) =>
                      p.product_data?.metadata?.tier?.toLowerCase() ===
                      upgradeTarget.toLowerCase()
                  )?.product_data
                }
                price={isAnnualSelected ? baseYearlyPrice : baseMonthlyPrice}
                currentPlan={currentPlan}
                isAnnualSelected={isAnnualSelected}
                onClick={() => {
                  trackPaywallButtonClicked(
                    `upgrade-to-${upgradeTarget.toLowerCase()}`
                  );
                  handleClickUpgrade(modalLabels["upgrade"]);
                }}
                isSignedIn={isSignedIn}
              />
            </div>
          </div>
          {!studentEmail && (
            <StudentDiscountBanner
              className="p-6 border-t"
              actionSource={MixpanelActionSource.PremiumFeatureModal}
            />
          )}
          <div className="sm:hidden block">{footer}</div>
        </div>
        <div className="sm:block hidden">{footer}</div>
      </ComposedModal>
    </StackedEmptyModal>
  );
}

export default PremiumFeatureModal;
